/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { enqueueErrorSnackbar, enqueueInfoSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { normalizeLanguageScope } from 'util/languageScope';

const DEFAULT_CONFIG = Object.freeze({
  enabled: false,
  type: 'category',
  exact: true,
  custom_dictionary: [],
});

const initialState = {
  entities: {},
  ids: [],
  savedEntities: {},
  savedIds: [],
  revision: 0,
  shopId: null,
  languageId: 'default',
  source: null,
  inheritedFrom: null,
  config: { ...DEFAULT_CONFIG },
  savedConfig: { ...DEFAULT_CONFIG },
  dictionary: [],
  dictionaryType: null,
  isFetching: false,
  isSaving: false,
  isDictionaryFetching: false,
  error: null,
  loadError: null,
  searchTerm: '',
  sortBy: 'query',
  sortOrder: 'asc',
};

const normalizeQuery = value => String(value || '').toLowerCase()
  .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeEntities = (items) => {
  const entities = {};
  const ids = [];
  (items || []).forEach((item) => {
    entities[item._id] = item;
    ids.push(item._id);
  });
  return { entities, ids };
};

const errorMessage = (error, fallbackKey) => (
  error?.body?.message
  || error?.response?.data?.message
  || error?.message
  || getLocalizedMessage(fallbackKey, { entity: 'taxonomy' })
);

const requestScope = (value, fallbackLanguage = 'default') => (
  value && typeof value === 'object'
    ? {
      shopId: value.shopId,
      languageId: normalizeLanguageScope(value.languageId, fallbackLanguage),
    }
    : { shopId: value, languageId: normalizeLanguageScope(fallbackLanguage) }
);

const actionLanguage = action => normalizeLanguageScope(
  action.payload?.language_id
    ?? (action.meta?.arg && typeof action.meta.arg === 'object'
      ? action.meta.arg.languageId
      : 'default'),
);
const actionShop = (action) => {
  const argument = action.meta?.arg;
  return argument && typeof argument === 'object' && argument.shopId != null
    ? String(argument.shopId)
    : null;
};
const matchesCurrentScope = (state, action) => (
  actionLanguage(action) === state.languageId
  && (!state.shopId || !actionShop(action) || actionShop(action) === String(state.shopId))
);
const matchesCurrentDictionary = (state, action) => (
  matchesCurrentScope(state, action)
  && (!action.meta?.arg?.type || action.meta.arg.type === state.config.type)
);

const resetScope = (state, { shopId = null, languageId = 'default' }) => {
  const normalizedShopId = shopId == null ? null : String(shopId);
  const normalizedLanguageId = normalizeLanguageScope(languageId);
  if (state.shopId === normalizedShopId && state.languageId === normalizedLanguageId) return;
  state.shopId = normalizedShopId;
  state.languageId = normalizedLanguageId;
  state.entities = {};
  state.ids = [];
  state.savedEntities = {};
  state.savedIds = [];
  state.revision = 0;
  state.config = { ...DEFAULT_CONFIG };
  state.savedConfig = { ...DEFAULT_CONFIG };
  state.dictionary = [];
  state.dictionaryType = null;
  state.source = null;
  state.inheritedFrom = null;
  state.isFetching = false;
  state.isSaving = false;
  state.isDictionaryFetching = false;
  state.error = null;
  state.loadError = null;
  state.searchTerm = '';
};

export const loadTaxonomies = createAsyncThunk(
  'taxonomies/load',
  async (scope, { extra: { serviceRegistry }, dispatch }) => {
    try {
      if (!serviceRegistry.taxonomyApi) throw new Error('Taxonomy API is unavailable.');
      const { shopId, languageId } = requestScope(scope);
      const response = await serviceRegistry.taxonomyApi.load(shopId, languageId);
      return { ...response, language_id: response?.language_id || languageId };
    } catch (error) {
      dispatch(enqueueErrorSnackbar(
        errorMessage(error, 'notifications.error.fetchingError'),
        { variant: 'error' },
      ));
      throw error;
    }
  },
);

export const loadTaxonomyDictionary = createAsyncThunk(
  'taxonomies/loadDictionary',
  async ({ shopId, type, languageId = 'default' }, { extra: { serviceRegistry } }) => {
    if (!serviceRegistry.taxonomyApi) throw new Error('Taxonomy API is unavailable.');
    const language = normalizeLanguageScope(languageId);
    const response = await serviceRegistry.taxonomyApi.loadDictionary(shopId, type, language);
    return { ...response, language_id: response?.language_id || language };
  },
);

export const saveTaxonomies = createAsyncThunk(
  'taxonomies/save',
  async (scope, {
    extra: { serviceRegistry }, dispatch, getState, rejectWithValue,
  }) => {
    try {
      if (!serviceRegistry.taxonomyApi) throw new Error('Taxonomy API is unavailable.');
      const state = getState().taxonomies;
      const { shopId, languageId } = requestScope(scope, state.languageId);
      if (languageId !== state.languageId
        || (state.shopId && String(shopId) !== String(state.shopId))) {
        throw new Error('Taxonomy shop or language changed before publication.');
      }
      const taxonomies = state.ids.map(id => state.entities[id]);
      const invalid = taxonomies.some(item => (
        !normalizeQuery(item.query)
        || !Array.isArray(item.taxonomies)
        || item.taxonomies.length === 0
      ));
      const unique = new Set(taxonomies.map(item => normalizeQuery(item.query)));
      if (invalid || unique.size !== taxonomies.length) {
        const message = getLocalizedMessage('taxonomy.validation.fixMappings', {});
        dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
        return rejectWithValue(message);
      }
      const payload = taxonomies.map(item => ({
        query: String(item.query).trim(),
        taxonomies: item.taxonomies.map(path => String(path).trim()).filter(Boolean),
        manual: item.manual !== false,
      }));
      const response = await serviceRegistry.taxonomyApi.save(
        shopId, payload, state.config, languageId,
      );
      dispatch(enqueueInfoSnackbar(
        getLocalizedMessage('taxonomy.notifications.published', {}),
        { variant: 'success' },
      ));
      return { ...response, language_id: response?.language_id || languageId };
    } catch (error) {
      const message = errorMessage(error, 'notifications.error.savingError');
      dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
      throw error;
    }
  },
);

const taxonomiesSlice = createSlice({
  name: 'taxonomies',
  initialState,
  reducers: {
    addTaxonomy: (state) => {
      const id = `temp-${Date.now()}-${state.ids.length}`;
      state.entities[id] = {
        _id: id,
        query: '',
        taxonomies: [],
        manual: true,
        _isNew: true,
      };
      state.ids.unshift(id);
    },
    updateTaxonomy: (state, { payload: { id, changes } }) => {
      if (state.entities[id]) {
        state.entities[id] = { ...state.entities[id], ...changes, _isModified: true };
      }
    },
    deleteTaxonomy: (state, { payload }) => {
      state.ids = state.ids.filter(id => id !== payload);
      delete state.entities[payload];
    },
    updateConfig: (state, { payload }) => {
      state.config = { ...state.config, ...payload };
    },
    discardChanges: (state) => {
      state.entities = { ...state.savedEntities };
      state.ids = [...state.savedIds];
      state.config = { ...state.savedConfig };
      state.error = null;
    },
    setSearchTerm: (state, { payload }) => {
      state.searchTerm = payload;
    },
    setSort: (state, { payload }) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload.sortOrder;
    },
    setLanguage: (state, { payload }) => resetScope(state, {
      shopId: state.shopId, languageId: payload,
    }),
    setScope: (state, { payload }) => resetScope(state, payload || {}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTaxonomies.pending, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        state.isFetching = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadTaxonomies.fulfilled, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        const { payload } = action;
        if (Number(payload.revision || 0) < state.revision) return;
        const { entities, ids } = normalizeEntities(payload.taxonomies);
        state.entities = entities;
        state.ids = ids;
        state.savedEntities = { ...entities };
        state.savedIds = [...ids];
        state.revision = Number(payload.revision || 0);
        state.source = payload.source || null;
        state.inheritedFrom = payload.inherited_from || null;
        state.config = { ...DEFAULT_CONFIG, ...(payload.config || {}) };
        state.savedConfig = { ...state.config };
        state.isFetching = false;
      })
      .addCase(loadTaxonomies.rejected, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        const { error } = action;
        state.error = error.message;
        state.loadError = error.message;
        state.isFetching = false;
      })
      .addCase(loadTaxonomyDictionary.pending, (state, action) => {
        if (!matchesCurrentDictionary(state, action)) return;
        state.isDictionaryFetching = true;
      })
      .addCase(loadTaxonomyDictionary.fulfilled, (state, action) => {
        if (!matchesCurrentDictionary(state, action)) return;
        const { payload } = action;
        state.dictionary = payload.dictionary || [];
        state.dictionaryType = payload.type || null;
        state.isDictionaryFetching = false;
      })
      .addCase(loadTaxonomyDictionary.rejected, (state, action) => {
        if (!matchesCurrentDictionary(state, action)) return;
        const { error } = action;
        state.error = error.message;
        state.isDictionaryFetching = false;
      })
      .addCase(saveTaxonomies.pending, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveTaxonomies.fulfilled, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        const { payload } = action;
        if (Number(payload.revision || 0) < state.revision) return;
        const { entities, ids } = normalizeEntities(payload.taxonomies);
        state.entities = entities;
        state.ids = ids;
        state.savedEntities = { ...entities };
        state.savedIds = [...ids];
        state.revision = Number(payload.revision || state.revision);
        state.source = payload.source || state.source;
        state.inheritedFrom = null;
        state.config = { ...DEFAULT_CONFIG, ...(payload.config || state.config) };
        state.savedConfig = { ...state.config };
        state.isSaving = false;
      })
      .addCase(saveTaxonomies.rejected, (state, action) => {
        if (!matchesCurrentScope(state, action)) return;
        const { error, payload } = action;
        state.error = payload || error.message;
        state.isSaving = false;
      });
  },
});

export const { actions } = taxonomiesSlice;

export const selectAllTaxonomies = createSelector(
  state => state.taxonomies.entities,
  state => state.taxonomies.ids,
  (entities, ids) => ids.map(id => entities[id]),
);

export const selectFilteredSortedTaxonomies = createSelector(
  selectAllTaxonomies,
  state => state.taxonomies.searchTerm,
  state => state.taxonomies.sortBy,
  state => state.taxonomies.sortOrder,
  (taxonomies, searchTerm, sortBy, sortOrder) => {
    const search = searchTerm.toLowerCase();
    const filtered = taxonomies.filter(item => (
      String(item.query).toLowerCase().includes(search)
      || (item.taxonomies || []).join(' ').toLowerCase().includes(search)
    ));
    const [newRows, persisted] = _.partition(filtered, item => item._isNew);
    return [...newRows, ..._.orderBy(persisted, [sortBy], [sortOrder])];
  },
);

export const selectHasInvalidTaxonomies = createSelector(
  selectAllTaxonomies,
  (taxonomies) => {
    const queries = taxonomies.map(item => normalizeQuery(item.query));
    return queries.some((query, index) => (
      !query
      || queries.indexOf(query) !== index
      || !Array.isArray(taxonomies[index].taxonomies)
      || taxonomies[index].taxonomies.length === 0
    ));
  },
);

export const selectHasChanges = createSelector(
  selectAllTaxonomies,
  state => state.taxonomies.savedIds,
  state => state.taxonomies.config,
  state => state.taxonomies.savedConfig,
  (taxonomies, savedIds, config, savedConfig) => (
    taxonomies.some(item => item._isNew || item._isModified)
    || taxonomies.length !== savedIds.length
    || !_.isEqual(config, savedConfig)
  ),
);

export default taxonomiesSlice.reducer;
